class AcousticTreatmentCalculator {
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;

    // Form elements
    this.lengthInput = el.querySelector('[data-room-length]');
    this.widthInput = el.querySelector('[data-room-width]');
    this.heightInput = el.querySelector('[data-room-height]');
    this.productSelect = el.querySelector('[data-product-select]');
    this.thicknessSelect = el.querySelector('[data-thickness-select]');
    this.reverbSelect = el.querySelector('[data-reverb-select]');
    this.timeDescriptions = el.querySelectorAll('[data-time-description]');

    // Result elements
    this.resultBox = el.querySelector('[data-result-box]');
    this.resultImage = el.querySelector('[data-result-image]');
    this.resultImageWrap = el.querySelector('[data-result-image-wrap]');
    this.resultTitle = el.querySelector('[data-result-title]');
    this.resultCurrentReverb = el.querySelector('[data-result-current-reverb]');
    this.resultHeadline = el.querySelector('[data-result-headline]');
    this.resultSuccessMsg = el.querySelector('[data-result-success]');
    this.resultFailMsg = el.querySelector('[data-result-fail]');
    this.resultFailTime = el.querySelector('[data-result-fail-time]');
    this.resultLinks = el.querySelectorAll('[data-result-url]');
    this.resultViewBtn = el.querySelector('[data-result-view-btn]');

    // State
    this.products = this._loadProducts();
    this.activeProduct = null;
    this.absorptionCoefficient = 0;

    this._init();
  }

  _loadProducts() {
    const el = document.getElementById(`AcousticProducts-${this.sectionId}`);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('Acoustic calculator: failed to parse product data', e);
      return [];
    }
  }

  _init() {
    this.products.forEach(product => {
      const option = document.createElement('option');
      option.value = product.handle;
      option.textContent = product.name;
      this.productSelect.appendChild(option);
    });

    this._updateTimeDescription();

    this.lengthInput.addEventListener('input', () => this._calculate());
    this.widthInput.addEventListener('input', () => this._calculate());
    this.heightInput.addEventListener('input', () => this._calculate());

    this.productSelect.addEventListener('change', () => {
      this._onProductChange(this.productSelect.value);
    });

    this.thicknessSelect.addEventListener('change', () => {
      this.absorptionCoefficient = parseFloat(this.thicknessSelect.value) || 0;
      this._calculate();
    });

    this.reverbSelect.addEventListener('change', () => {
      this._updateTimeDescription();
      this._calculate();
    });
  }

  _onProductChange(handle) {
    this.activeProduct = this.products.find(p => p.handle === handle) || null;
    this.absorptionCoefficient = 0;

    // Reset thickness select
    this.thicknessSelect.innerHTML = '';
    const placeholder = this.thicknessSelect.dataset.placeholder || 'Material Thickness';

    if (!this.activeProduct) {
      this.thicknessSelect.setAttribute('disabled', true);
      this._addOption(this.thicknessSelect, '', placeholder, true);
      this._hideResult();
      return;
    }

    const thicknesses = [
      { label: this.activeProduct.thickness_1, value: this.activeProduct.absorption_coefficient_1 },
      { label: this.activeProduct.thickness_2, value: this.activeProduct.absorption_coefficient_2 },
      { label: this.activeProduct.thickness_3, value: this.activeProduct.absorption_coefficient_3 },
    ].filter(t => t.label);

    if (!thicknesses.length) {
      this.thicknessSelect.setAttribute('disabled', true);
      this._addOption(this.thicknessSelect, '', placeholder, true);
      this._hideResult();
      return;
    }

    this.thicknessSelect.removeAttribute('disabled');

    if (thicknesses.length > 1) {
      this._addOption(this.thicknessSelect, '', placeholder, true);
    }

    thicknesses.forEach(t => {
      this._addOption(this.thicknessSelect, t.value, t.label);
    });

    // Auto-select if only one option
    if (thicknesses.length === 1) {
      this.thicknessSelect.value = thicknesses[0].value;
      this.absorptionCoefficient = thicknesses[0].value;
    }

    this._calculate();
  }

  _addOption(select, value, text, disabled = false) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = text;
    if (disabled) {
      opt.disabled = true;
      opt.selected = true;
    }
    select.appendChild(opt);
  }

  _updateTimeDescription() {
    const current = this.reverbSelect.value;
    this.timeDescriptions.forEach(el => {
      el.classList.toggle('hidden', el.dataset.timeDescription !== current);
    });
  }

  _getSurfaces() {
    const l = parseFloat(this.lengthInput.value) || 0;
    const w = parseFloat(this.widthInput.value) || 0;
    const h = parseFloat(this.heightInput.value) || 0;
    return { l, w, h,
      ceiling: l * w, floor: l * w,
      wall1: l * h, wall2: l * h,
      wall3: w * h, wall4: w * h,
    };
  }

  _getCurrentAbsorption(s) {
    return s.ceiling * 0.05 + s.floor * 0.10 +
           s.wall1 * 0.05 + s.wall2 * 0.05 +
           s.wall3 * 0.05 + s.wall4 * 0.05;
  }

  _calculate() {
    const reverbTime = parseFloat(this.reverbSelect.value);
    const s = this._getSurfaces();

    if (!s.l || !s.w || !s.h || !this.activeProduct || !this.absorptionCoefficient || !reverbTime) {
      this._hideResult();
      return;
    }

    const volume = s.l * s.w * s.h;
    const currentAbsorption = this._getCurrentAbsorption(s);
    const currentReverbTime = parseFloat(((0.05 * volume) / currentAbsorption).toFixed(2));

    let sqFt;
    if (this.activeProduct.is_baffle && this.activeProduct.baffle_sq_ft) {
      const sabinesNeeded = ((0.05 * volume) - (currentAbsorption * reverbTime)) / reverbTime;
      sqFt = (sabinesNeeded / this.absorptionCoefficient) * this.activeProduct.baffle_sq_ft;
    } else {
      sqFt = ((0.05 * volume) - (currentAbsorption * reverbTime)) / (reverbTime * this.absorptionCoefficient);
    }

    this._showResult(Math.ceil(sqFt), currentReverbTime, reverbTime);
  }

  _showResult(sqFt, currentReverbTime, targetReverbTime) {
    const url = this.activeProduct.url;
    const image = this.activeProduct.image;
    const name = this.activeProduct.name;

    this.resultLinks.forEach(link => link.setAttribute('href', url || '#'));
    if (this.resultTitle) this.resultTitle.textContent = name;

    // Graceful image fallback: hide the image block entirely when none is available
    if (this.resultImageWrap) {
      if (image) {
        this.resultImage.src = image;
        this.resultImage.alt = name;
        this.resultImageWrap.classList.remove('hidden');
      } else {
        this.resultImage.removeAttribute('src');
        this.resultImageWrap.classList.add('hidden');
      }
    }

    const fmt = n => n === 1 ? `${n} second` : `${n} seconds`;
    if (this.resultCurrentReverb) this.resultCurrentReverb.textContent = fmt(currentReverbTime);

    if (sqFt > 0) {
      this.resultSuccessMsg?.classList.remove('hidden');
      this.resultFailMsg?.classList.add('hidden');
      this._renderHeadline(sqFt);
      this.resultViewBtn?.classList.remove('hidden');
    } else {
      this.resultSuccessMsg?.classList.add('hidden');
      this.resultFailMsg?.classList.remove('hidden');
      if (this.resultFailTime) this.resultFailTime.textContent = fmt(targetReverbTime);
      this.resultViewBtn?.classList.add('hidden');
    }

    this.resultBox?.classList.remove('hidden');
  }

  _renderHeadline(sqFt) {
    if (!this.resultHeadline) return;
    const template = this.resultHeadline.dataset.headlineTemplate || 'You need [sqft] to hit your target reverberation time.';
    const unit = this.resultHeadline.dataset.sqftUnit || 'sq ft';
    const value = `${new Intl.NumberFormat().format(sqFt)} ${unit}`.trim();

    // Build with DOM nodes so the headline template text is never parsed as HTML
    this.resultHeadline.textContent = '';
    const [before, after] = template.split('[sqft]');
    this.resultHeadline.append(document.createTextNode(before ?? ''));
    const valueSpan = document.createElement('span');
    valueSpan.className = 'acoustic-treatment-calculator__headline-value';
    valueSpan.textContent = value;
    this.resultHeadline.append(valueSpan);
    if (after !== undefined) this.resultHeadline.append(document.createTextNode(after));
  }

  _hideResult() {
    this.resultBox?.classList.add('hidden');
  }
}

document.querySelectorAll('[data-acoustic-treatment-calculator]').forEach(el => {
  new AcousticTreatmentCalculator(el);
});